import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
} from '@mui/material';
import { includes, isEmpty, sortBy } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { capitalizeName } from 'common/utils/titleCase';
import { markCatalogAvailability } from 'store/thunks/catalogThunks';

import AlertPartnersCheckbox, { ALERT_PARTNERS_CHECKBOX } from './AlertPartnersCheckbox';
import CatalogDialogHeader from './CatalogDialogHeader';
import { formatDrugName } from '../utils/format';

const EditCatalogAvailabilityDialog = ({ item, drugName, allPartners }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);
  const [partnerIds, setPartnerIds] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertPartners, setAlertPartners] = useState(false);

  useEffect(() => {
    if (!open) return;

    setIsAvailable(item.isAvailable);
    setIsActive(!!item.isPartnerActive);
    setPartnerIds(item.isAvailable ? [] : (item.whitelisted ?? []));
    setAlertPartners(false);
  }, [open, item]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const hasWhitelist = !isAvailable && !isEmpty(partnerIds);
  const isActiveCheckboxDisabled = !isAvailable && isEmpty(partnerIds);

  const handleSave = () => {
    setSaving(true);

    dispatch(
      markCatalogAvailability({
        catalogId: item.catalogId,
        isAvailable: hasWhitelist ? true : isAvailable,
        ...(isActiveCheckboxDisabled ? {} : { isActive, alertPartners }),
        partnerIds: hasWhitelist ? partnerIds : [],
        scope: hasWhitelist ? 'INCLUDE' : 'ALL',
      })
    )
      .then((response) => {
        if (response) {
          enqueueSnackbar('Catalog Updated Successfully', { variant: 'success' });
          handleClose();
        }
      })
      .finally(() => setSaving(false));
  };

  // Case-insensitive: a raw sort puts every capitalised name above every lowercase one.
  const sortedPartners = sortBy(allPartners, (partner) =>
    String(partner?.partnerName ?? '').toLowerCase()
  );

  // Only Cancel and the header's X close this -- a click on the backdrop
  // shouldn't discard an in-progress edit.
  const handleDialogClose = (_, reason) => {
    if (saving || reason === 'backdropClick') return;
    handleClose();
  };

  return (
    <>
      <Button variant='outlined' onClick={handleOpen}>
        Edit
      </Button>

      <Dialog open={open} onClose={handleDialogClose} maxWidth='sm' fullWidth>
        <CatalogDialogHeader
          title={`Edit ${formatDrugName(drugName)} Availability`}
          onClose={handleDialogClose}
        />

        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={isAvailable}
                onChange={(event) => setIsAvailable(event.target.checked)}
              />
            }
            label='Mark Available for All Partners'
            sx={{ mt: 1 }}
          />

          {!isAvailable && (
            <Box sx={{ my: 2 }}>
              <FormControl fullWidth variant='outlined' required sx={{ mt: 1 }}>
                <InputLabel id='EditCatalogAvailabilityDialog-partners-label' shrink>
                  Whitelist Partners
                </InputLabel>
                <Select
                  labelId='EditCatalogAvailabilityDialog-partners-label'
                  id='EditCatalogAvailabilityDialog-partners-input'
                  multiple
                  input={<OutlinedInput label='Whitelist Partners' />}
                  value={partnerIds}
                  onChange={(event) => setPartnerIds(event.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((partnerUuid) => {
                        const partner = allPartners.find((p) => p.partnerUuid === partnerUuid);
                        return partner ? (
                          <Chip key={partnerUuid} label={capitalizeName(partner.partnerName)} />
                        ) : null;
                      })}
                    </Box>
                  )}
                  notched
                  MenuProps={{ sx: { maxHeight: 400 } }}
                >
                  {sortedPartners.map(({ partnerUuid, partnerName }) => (
                    <MenuItem key={partnerUuid} value={partnerUuid}>
                      <Checkbox checked={includes(partnerIds, partnerUuid)} />
                      <ListItemText primary={capitalizeName(partnerName)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {isActiveCheckboxDisabled ? (
            <span />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isActive}
                    onChange={(event) => setIsActive(event.target.checked)}
                  />
                }
                label='Also Mark as Active'
              />
              <AlertPartnersCheckbox
                id='EditCatalogAvailabilityDialog-alert-partners'
                text={ALERT_PARTNERS_CHECKBOX}
                checked={alertPartners}
                onChange={setAlertPartners}
              />
            </Box>
          )}
          <Box>
            <Button variant='outlined' onClick={handleClose} disabled={saving} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <LoadingButton
              loading={saving}
              variant='contained'
              onClick={handleSave}
              disabled={saving}
            >
              Submit
            </LoadingButton>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

EditCatalogAvailabilityDialog.propTypes = {
  item: PropTypes.shape({
    catalogId: PropTypes.string.isRequired,
    isAvailable: PropTypes.bool,
    isPartnerActive: PropTypes.bool,
    whitelisted: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  drugName: PropTypes.string.isRequired,
  allPartners: PropTypes.arrayOf(
    PropTypes.shape({ partnerUuid: PropTypes.string, partnerName: PropTypes.string })
  ),
};

EditCatalogAvailabilityDialog.defaultProps = {
  allPartners: [],
};

export default EditCatalogAvailabilityDialog;
